import styled from '@emotion/styled';
import Typography from '@mui/material/Typography';
import slugify from '@sindresorhus/slugify';
import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useNavigate } from 'react-router-dom';

import ArrowContainedIcon from 'assets/icons/arrow_contained.svg?react';
import ChevronIcon from 'assets/icons/chevron_right.svg?react';
import HiddenLink from 'components/HiddenLink';
import SunIcon from 'assets/icons/famicons_sunny.svg?react';
import MoonIcon from 'assets/icons/famicons_moon.svg?react';
import ScheduleIcon from 'assets/icons/icon_schedule.svg?react';
import LaptopIcon from 'assets/icons/ic_baseline-laptop.svg?react';
import formatPriceRange from 'helpers/formatPriceRange';
import IconButton from '@mui/material/IconButton';
import { useDispatch } from 'react-redux';
import { isAuthenticated } from 'helpers/authToken';
import NeedAuthDialog from 'components/NeedAuthDialog';
import StarIcon from 'assets/icons/star.svg?react';
import CheckedStarIcon from 'assets/icons/star_checked.svg?react';
import {
  addEducationProgramToFavorites,
  removeEducationProgramFromFavorites
} from 'actions/educations';

const Card = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: 18,
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: 24,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  cursor: 'pointer',
  paddingBottom: 45
}));

const LinkStyled = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: 'rgba(238, 237, 242, 1)',
  color: 'rgba(10, 10, 10, 1)',
  borderRadius: 8,
  padding: 10,
  marginLeft: -4,
  marginRight: -4,
  marginBottom: 8,
  cursor: 'pointer',
  '& svg': {
    width: 20
  }
}));

const LinkTitleStyled = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: 16,
  lineHeight: 1.5,
  color: 'rgba(10, 10, 10, 1)'
}));

const ListItemTitle = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: 20,
  lineHeight: '24px',
  color: 'rgba(68, 68, 68, 1)',
  position: 'relative',
  paddingRight: 25
}));

const TagsWrapper = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginTop: 8,
  marginBottom: 16
}));

const Tag = styled('span')(({ type }) => ({
  fontSize: 14,
  color: 'rgba(10, 10, 10, 1)',
  borderRadius: 38,
  padding: '4px 10px',
  fontFamily: 'Rubik-Regular',
  position: 'relative',
  paddingLeft: 20,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: 10,
    width: 6,
    height: 6,
    marginTop: -3,
    borderRadius: '50%'
  },
  ...((type === 1 || ['Фаховий молодший бакалавр', 'Бакалавр'].includes(type)) && {
    backgroundColor: 'rgba(130, 130, 130, 0.2)',
    '&:before': {
      backgroundColor: 'rgba(130, 130, 130, 1)'
    }
  }),
  ...((type === 2 || ['Бакалавр'].includes(type)) && {
    backgroundColor: 'rgba(121, 201, 18, 0.2)',
    '&:before': {
      backgroundColor: 'rgba(121, 201, 18, 1)'
    }
  }),
  ...((type === 3 || ['Спеціаліст', 'Магістр', 'Експерт'].includes(type)) && {
    backgroundColor: 'rgba(255, 191, 0, 0.2)',
    '&:before': {
      backgroundColor: 'rgba(255, 191, 0, 1)'
    }
  })
}));

const CardRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  gap: 5,
  marginBottom: 7
}));

const CardField = styled(Typography)(() => ({
  fontSize: 15,
  fontWeight: 500,
  lineHeight: 1.5,
  color: 'rgba(10, 10, 10, 1)'
}));

const CardValue = styled(Typography)(() => ({
  fontSize: 15,
  fontWeight: 400,
  lineHeight: 1.5,
  color: 'rgba(10, 10, 10, 1)'
}));

const ArrowContainedIconStyled = styled(ArrowContainedIcon)(() => ({
  position: 'absolute',
  right: 20,
  bottom: 10
}));

const FavoriteButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: -10,
  right: 0,
  zIndex: 10,
  '& svg': {
    width: 32,
    height: 32
  }
}));

const ChevronHidden = styled(ChevronIcon)(() => ({
  visibility: 'hidden'
}));

const StudyFormItem = styled('span')(() => ({
  alignItems: 'center',
  gap: 4,
  '& svg': {
    width: 16,
    height: 16,
    position: 'relative',
    top: 3
  }
}));

const SpecialtyTag = styled('span')(() => ({
  fontSize: 13,
  color: 'rgba(10, 10, 10, 1)',
  backgroundColor: 'rgba(238, 237, 242, 1)',
  borderRadius: 6,
  padding: '4px 8px',
  fontFamily: 'Rubik-Regular',
  fontWeight: 500,
  display: 'inline-block'
}));

const EducationProgramCard = ({ item }) => {
  const t = useTranslate('EducationPrograms');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [needAuth, setNeedAuth] = React.useState(false);
  const [isFavorite, setIsFavorite] = React.useState(item?.isFavorite);

  const { _id, institution, programName, degree, financingType, educationForm, specialties } = item;

  // Calculate price range from all available education forms
  const getPriceRange = () => {
    const prices = [];
    const currencies = [];

    if (educationForm?.fullTime?.available && educationForm.fullTime.costValue) {
      prices.push(Number(educationForm.fullTime.costValue));
      if (educationForm.fullTime.costCurrency) {
        currencies.push(educationForm.fullTime.costCurrency);
      }
    }
    if (educationForm?.evening?.available && educationForm.evening.costValue) {
      prices.push(Number(educationForm.evening.costValue));
      if (educationForm.evening.costCurrency) {
        currencies.push(educationForm.evening.costCurrency);
      }
    }
    if (educationForm?.extramural?.available && educationForm.extramural.costValue) {
      prices.push(Number(educationForm.extramural.costValue));
      if (educationForm.extramural.costCurrency) {
        currencies.push(educationForm.extramural.costCurrency);
      }
    }
    if (educationForm?.distanceLearning?.available && educationForm.distanceLearning.costValue) {
      prices.push(Number(educationForm.distanceLearning.costValue));
      if (educationForm.distanceLearning.costCurrency) {
        currencies.push(educationForm.distanceLearning.costCurrency);
      }
    }

    if (prices.length === 0) return null;

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    // Use the first available currency, assuming they're all the same
    const currency = currencies.length > 0 ? t(currencies[0]) : '';

    return {
      min: minPrice,
      max: maxPrice,
      currency
    };
  };

  const priceRange = getPriceRange();

  const hasStudyForm = Boolean(
    educationForm?.fullTime?.available ||
      educationForm?.evening?.available ||
      educationForm?.distanceLearning?.available ||
      educationForm?.extramural?.available
  );

  const handleNavigate = () => {
    const slug = slugify(programName || 'program');
    navigate(`/education/programs/${slug}/${_id}`);
  };
  const href = React.useMemo(
    () => {
      const slug = slugify(programName || 'program');
      return `/education/programs/${slug}/${_id}`;
    },
    [programName, _id]
  );

  const handleClose = React.useCallback((e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setNeedAuth(false);
  }, []);

  const handleToggleFavorite = React.useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isAuthenticated()) {
        setNeedAuth(true);
        return;
      }

      const next = !isFavorite;
      setIsFavorite(next);

      if (next) {
        dispatch(addEducationProgramToFavorites(_id));
      } else {
        dispatch(removeEducationProgramFromFavorites(_id));
      }
    },
    [dispatch, _id, isFavorite]
  );

  return (
    <Card onClick={handleNavigate}>
      <NeedAuthDialog handleClose={handleClose} needAuth={needAuth} />
      <HiddenLink href={href} />
      <LinkStyled>
        <LinkTitleStyled>{institution?.name}</LinkTitleStyled>
        <ChevronHidden />
      </LinkStyled>

      <ListItemTitle>
        {programName}

        <FavoriteButton onClick={handleToggleFavorite} aria-label="favorite" size="small">
          {isFavorite ? <CheckedStarIcon /> : <StarIcon />}
        </FavoriteButton>
      </ListItemTitle>

      <TagsWrapper>
        {[degree].map((l, idx) => (
          <Tag key={`${l}-${idx}`} type={l}>
            {l}
          </Tag>
        ))}
      </TagsWrapper>

      {Array.isArray(specialties) && specialties.length > 0 && (
        <TagsWrapper style={{ marginTop: 4, marginBottom: 12 }}>
          {specialties.map((specialty, idx) => (
            <SpecialtyTag key={`${specialty.code}-${idx}`}>
              {specialty.code}
              {specialty.name ? ` — ${specialty.name}` : ''}
            </SpecialtyTag>
          ))}
        </TagsWrapper>
      )}

      <CardRow>
        {institution ? (
          <CardField>{institution?.contactInfo?.katottgName + ' | '}</CardField>
        ) : null}
        <CardValue style={{ flex: 1 }}>
          {financingType?.budget
            ? t('InstitutionTypePublic') + ' ' + t('InstitutionType')
            : t('InstitutionTypePrivate') + ' ' + t('InstitutionType')}
        </CardValue>
      </CardRow>

      {hasStudyForm && (
        <CardRow>
          <CardField>{t('StudyFormHeadline')}:</CardField>
          <CardValue>
            {(() => {
              const parts = [];
              if (educationForm?.fullTime?.available) {
                parts.push(
                  <StudyFormItem key="fullTime">
                    <SunIcon />
                    {t('StudyFormFullTime')}
                  </StudyFormItem>
                );
              }
              if (educationForm?.evening?.available) {
                parts.push(
                  <StudyFormItem key="evening">
                    <MoonIcon />
                    {t('StudyFormEvening')}
                  </StudyFormItem>
                );
              }
              if (educationForm?.extramural?.available) {
                parts.push(
                  <StudyFormItem key="extramural">
                    <ScheduleIcon />
                    {t('StudyFormExtramural')}
                  </StudyFormItem>
                );
              }
              if (educationForm?.distance?.available) {
                parts.push(
                  <StudyFormItem key="distanceLearning">
                    <LaptopIcon />
                    {t('StudyFormDistance')}
                  </StudyFormItem>
                );
              }
              return parts.reduce((acc, el, idx) => {
                if (idx === 0) return [el];
                return acc.concat([' + ', el]);
              }, []);
            })()}
          </CardValue>
        </CardRow>
      )}

      {priceRange ? (
        <CardRow>
          <CardField>
            {formatPriceRange(priceRange.min, priceRange.max, priceRange.currency)}
          </CardField>
          <CardValue>{t('perYear')}</CardValue>
        </CardRow>
      ) : null}

      <ArrowContainedIconStyled />
    </Card>
  );
};

export default EducationProgramCard;
